import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import Search from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import _ from "lodash";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import InternalError from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import PageHeader from "../components/PageHeader/PageHeader";
import { useBot, useDeleteChat } from "../components/Sirius/SiriusQueries";

export function RecentChatsPage() {
  let params = useParams();
  const botId = params.botId as string;
  const { data: bot, isLoading, isError } = useBot(botId);
  const chats = _.filter(bot?.chats, (chat) => chat.id !== "new");
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);
  const [hoveredChatId, setHoveredChatId] = useState<string>("");
  const { mutate: handleDeleteChat } = useDeleteChat(botId);

  const filteredChats =
    chats?.filter((chat) =>
      chat.title?.toLowerCase().includes(searchText.toLowerCase())
    ) ?? [];

  const selectedAll = selectedChatIds.length === chats?.length;

  const handleChatClick = (chatId: string) => {
    if (selectionMode) {
      setSelectedChatIds((prev) =>
        prev.includes(chatId)
          ? prev.filter((id) => id !== chatId)
          : [...prev, chatId]
      );
    } else {
      navigate(`/sirius/chat/${chatId}`);
    }
  };

  const handleChexboxSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    chatId: string
  ) => {
    e.stopPropagation();
    if (selectedChatIds.includes(chatId)) {
      const newSelection = selectedChatIds.filter((id) => id !== chatId);
      setSelectedChatIds(newSelection);
      if (newSelection.length === 0) setSelectionMode(false);
    } else {
      setSelectedChatIds([...selectedChatIds, chatId]);
      setSelectionMode(true);
    }
  };

  function getContent() {
    if (isError) {
      return <InternalError />;
    } else if (isLoading) {
      return <Loading />;
    } else {
      return (
        <div className="flex flex-col flex-1 items-center overflow-hidden p-4 relative">
          <div className="flex flex-col w-full">
            <div className="flex w-full items-center justify-between gap-10 sticky">
              <TextField
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  },
                }}
                className="flex flex-1"
                placeholder="Effectuer une recherche..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <Button
                onClick={() => navigate("chat/new")}
                variant="contained"
                className="flex items-center gap-2"
              >
                <Add />
                Nouvelle conversation
              </Button>
            </div>
            <div className="flex justify-between items-center w-full pt-8">
              <p className="text-sm text-gray-600">
                {searchText
                  ? `${filteredChats.length} résultats`
                  : `Vous avez eu ${filteredChats.length} conversations avec l'assistant ${bot?.name}`}
              </p>
              {!selectionMode ? (
                <Button
                  onClick={() => setSelectionMode(true)}
                  variant="outlined"
                >
                  Selectionner
                </Button>
              ) : (
                <div className="flex gap-2 items-center">
                  <span className="text-sm text-gray-600">
                    {selectedChatIds.length} conversations sélectionnées
                  </span>
                  {!selectedAll && (
                    <Button
                      onClick={() =>
                        setSelectedChatIds(filteredChats.map((chat) => chat.id))
                      }
                      className="text-[#4c4fa2]"
                    >
                      Tout selectionner
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setSelectionMode(false);
                      setSelectedChatIds([]);
                    }}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => {
                      selectedChatIds.forEach((chatId) =>
                        handleDeleteChat(chatId)
                      );
                      setSelectedChatIds([]);
                      setSelectionMode(false);
                    }}
                    className="text-red-600 hover:text-red-800"
                    disabled={selectedChatIds.length === 0}
                  >
                    Supprimer
                  </Button>
                </div>
              )}
            </div>
          </div>

          <Stack className="w-full overflow-auto lg:max-w-[70%] mx-auto">
            {filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleChatClick(chat.id)}
                onMouseEnter={() => setHoveredChatId(chat.id)}
                onMouseLeave={() => setHoveredChatId("")}
                className="flex items-center justify-between py-4 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  {(selectionMode || hoveredChatId === chat.id) && (
                    <Tooltip title="Select" placement="top">
                      <input
                        type="checkbox"
                        checked={selectedChatIds.includes(chat.id)}
                        onChange={(e) => handleChexboxSelect(e, chat.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4"
                      />
                    </Tooltip>
                  )}
                  <span className="font-medium">{chat.title}</span>
                </div>

                {!selectionMode && hoveredChatId === chat.id && (
                  <Tooltip title="Delete chat" placement="right">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteChat(chat.id);
                      }}
                      className="hover:bg-gray-100 rounded-full"
                    >
                      <Delete
                        fontSize="medium"
                        className="text-gray-500 hover:text-gray-900"
                      />
                    </Button>
                  </Tooltip>
                )}
              </div>
            ))}
          </Stack>
        </div>
      );
    }
  }

  return (
    <DashboardLayout>
      <Container maxWidth={false} className="overflow-hidden h-full">
        <Stack
          spacing={2}
          className="flex flex-col my-2 h-full overflow-hidden"
        >
          <PageHeader title={bot?.name} />
          {getContent()}
        </Stack>
      </Container>
    </DashboardLayout>
  );
}
